import { MousePointer2, Type, Pencil, Eraser, Undo2, Redo2, Trash2 } from "lucide-react";

/**
 * PDFAnnotationToolbar - Tool selection for PDF annotations
 * 
 * Tools: Select, Add Text, Draw, Eraser, Undo, Redo, Clear Page
 */
export default function PDFAnnotationToolbar({
  activeTool,
  onToolChange,
  onUndo,
  onRedo,
  onClearPage,
  canUndo,
  canRedo,
  saveStatus, // "idle" | "saving" | "saved" | "error"
  strokeWidth,
  onStrokeWidthChange,
  fontSize,
  onFontSizeChange,
}) {
  const tools = [
    { id: "select", icon: MousePointer2, label: "Select / Move" },
    { id: "text", icon: Type, label: "Add Text" },
    { id: "draw", icon: Pencil, label: "Draw" },
    { id: "eraser", icon: Eraser, label: "Eraser" },
  ];

  return (
    <div 
      className="flex flex-wrap items-center gap-2 p-2 bg-white border border-[#E5E5E3] rounded-lg shadow-sm"
      data-testid="pdf-annotation-toolbar"
    >
      {/* Tool buttons */}
      <div className="flex items-center gap-1">
        {tools.map(tool => (
          <button
            key={tool.id}
            onClick={() => onToolChange(tool.id)}
            data-testid={`annotation-tool-${tool.id}`}
            title={tool.label}
            className={`p-2 rounded-lg transition-colors ${
              activeTool === tool.id
                ? "bg-[#205B5A] text-white"
                : "text-[#757675] hover:bg-[#FAF9F6] hover:text-[#1A1B1A]"
            }`}
          >
            <tool.icon className="w-4 h-4" />
          </button>
        ))}
      </div>

      {/* Separator */}
      <div className="w-px h-6 bg-[#E5E5E3]" />

      {/* Tool options */}
      {activeTool === "draw" && (
        <div className="flex items-center gap-2">
          <label className="text-[10px] text-[#757675] uppercase">Size</label>
          <select
            value={strokeWidth}
            onChange={e => onStrokeWidthChange(Number(e.target.value))}
            className="text-xs px-2 py-1 rounded border border-[#E5E5E3] bg-white"
          >
            <option value={1}>Fine</option>
            <option value={2}>Medium</option>
            <option value={4}>Thick</option>
            <option value={8}>Bold</option>
          </select>
        </div>
      )}

      {activeTool === "text" && (
        <div className="flex items-center gap-2">
          <label className="text-[10px] text-[#757675] uppercase">Size</label>
          <select
            value={fontSize}
            onChange={e => onFontSizeChange(Number(e.target.value))}
            className="text-xs px-2 py-1 rounded border border-[#E5E5E3] bg-white"
          >
            <option value={10}>Small</option>
            <option value={14}>Medium</option>
            <option value={18}>Large</option>
            <option value={24}>X-Large</option>
          </select>
        </div>
      )}

      {/* Separator */}
      <div className="w-px h-6 bg-[#E5E5E3]" />

      {/* Undo / Redo */}
      <div className="flex items-center gap-1">
        <button
          onClick={onUndo}
          disabled={!canUndo}
          title="Undo (Ctrl+Z)"
          data-testid="annotation-undo"
          className={`p-2 rounded-lg transition-colors ${
            canUndo
              ? "text-[#757675] hover:bg-[#FAF9F6] hover:text-[#1A1B1A]"
              : "text-[#E5E5E3] cursor-not-allowed"
          }`}
        >
          <Undo2 className="w-4 h-4" />
        </button>
        <button
          onClick={onRedo}
          disabled={!canRedo}
          title="Redo (Ctrl+Y)"
          data-testid="annotation-redo"
          className={`p-2 rounded-lg transition-colors ${
            canRedo
              ? "text-[#757675] hover:bg-[#FAF9F6] hover:text-[#1A1B1A]"
              : "text-[#E5E5E3] cursor-not-allowed"
          }`}
        >
          <Redo2 className="w-4 h-4" />
        </button>
      </div>

      {/* Separator */}
      <div className="w-px h-6 bg-[#E5E5E3]" />

      {/* Clear Page */}
      <button
        onClick={onClearPage}
        title="Clear Current Page"
        data-testid="annotation-clear-page"
        className="p-2 rounded-lg text-red-500 hover:bg-red-50 transition-colors"
      >
        <Trash2 className="w-4 h-4" />
      </button>

      {/* Save status */}
      <div className="ml-auto flex items-center gap-2">
        {saveStatus === "saving" && (
          <span className="text-[10px] text-[#757675] animate-pulse">Saving...</span>
        )}
        {saveStatus === "saved" && (
          <span className="text-[10px] text-[#205B5A]">Saved</span>
        )}
        {saveStatus === "error" && (
          <span className="text-[10px] text-red-500">Save failed</span>
        )}
      </div>
    </div>
  );
}
